import React, { useEffect, useState } from "react";

import {
  TextField,
  MenuItem,
  Typography,
  Box,
  Autocomplete,
  CircularProgress,
  IconButton,
  Tooltip
} from "@mui/material";

import SyncIcon from "@mui/icons-material/Sync";

import {
  useForm,
  Controller
} from "react-hook-form";

import { useRoles } from "../../context/RolesContext";
import { useDepartments } from "../../context/DepartmentsContext";
import { useEmployees } from "../../context/EmployeesContext";

const defaultRoleOptions = [
  { value: "super_admin", label: "Super Admin" },
  { value: "manager", label: "Manager" },
  { value: "employee", label: "Employee" },
  { value: "member", label: "Member" }
];

// Match the query against the full label or any individual word's start
// (e.g. "Shah" matches "Bhumit Shah"), normalizing whitespace/case.
const normalizeText = (value) => (value || "").toLowerCase().replace(/\s+/g, " ").trim();

const filterByLabel = (options, query, getLabel) => {
  const normalizedQuery = normalizeText(query);

  if (!normalizedQuery) {
    return options;
  }

  return options.filter((option) => {
    const label = normalizeText(getLabel(option));
    return label.startsWith(normalizedQuery) || label.split(" ").some((word) => word.startsWith(normalizedQuery));
  });
};

function EmployeeForm({
  employee,
  onSubmit
}) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors }
  } = useForm();

  const { roles } = useRoles();
  const { departments, loading: departmentsLoading, refreshDepartments } = useDepartments();
  const { employees: managers, loading: managersLoading } = useEmployees();

  const [syncMessage, setSyncMessage] = useState("");

  const managerId = watch("manager_id", employee?.manager_id || "");
  const departmentId = watch("department_id", employee?.department_id || "");

  const roleOptions = roles.length > 0
    ? roles.map((role) => ({
        value: role.role_name,
        label: role.role_name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
      }))
    : defaultRoleOptions;

  useEffect(() => {
    if (employee) {
      reset({
        employee_name: employee.employee_name,
        email: employee.email,
        designation: employee.designation || "",
        department_id: employee.department_id,
        role_name: employee.role_name || "member",
        manager_id: employee.manager_id || "",
        default_capacity_percent: employee.default_capacity_percent ?? 100,
      });
    }
    else {
      reset({
        employee_name: "",
        email: "",
        designation: "",
        department_id: "",
        role_name: "member",
        manager_id: "",
        default_capacity_percent: 100,
      });
    }
  }, [employee, reset]);

  const sortedManagers = managers
    .filter((manager) => manager.employee_id !== employee?.employee_id)
    .sort((a, b) => a.employee_name.localeCompare(b.employee_name));

  const sortedDepartments = [...departments].sort((a, b) =>
    a.department_name.localeCompare(b.department_name)
  );

  const handleSyncDepartment = () => {
    const designation = watch("designation")?.trim();
    if (!designation) {
      setSyncMessage("No designation set");
      return;
    }
    const match = departments.find(
      (d) => d.department_name.toLowerCase() === designation.toLowerCase()
    );
    if (match) {
      setValue("department_id", match.department_id, { shouldValidate: true });
      setSyncMessage(`Matched “${match.department_name}”`);
    } else {
      setSyncMessage(`No department found for “${designation}”`);
    }
    setTimeout(() => setSyncMessage(""), 3000);
  };

  return (
    <form
      id="employee-form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <input type="hidden" {...register("manager_id")} />
      <input type="hidden" {...register("department_id", { required: true })} />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2.2
        }}
      >
        <Typography
          sx={{
            fontSize: 13,
            color: "#475569",
            fontWeight: 600,
            mb: 0.2
          }}
        >
          Employee Information
        </Typography>

        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: "text.primary",
              mb: 0.8
            }}
          >
            Employee Name
          </Typography>

          <TextField
            fullWidth
            size="small"
            placeholder="Enter employee name"
            disabled={Boolean(employee)}
            error={Boolean(errors.employee_name)}
            helperText={errors.employee_name ? "Employee name is required" : ""}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.paper"
              },
              "& .MuiFormHelperText-root": {
                minHeight: 20,
                mt: 0.7
              }
            }}
            {...register("employee_name", { required: true })}
          />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: "text.primary",
              mb: 0.8
            }}
          >
            Email
          </Typography>

          <TextField
            fullWidth
            size="small"
            placeholder="Enter email address"
            disabled={Boolean(employee)}
            error={Boolean(errors.email)}
            helperText={errors.email ? "Email is required" : ""}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.paper"
              },
              "& .MuiFormHelperText-root": {
                minHeight: 20,
                mt: 0.7
              }
            }}
            {...register("email", { required: true })}
          />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: "text.primary",
              mb: 0.8
            }}
          >
            Designation
          </Typography>

          <TextField
            fullWidth
            size="small"
            placeholder="Enter designation / job title"
            disabled={Boolean(employee)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.paper"
              }
            }}
            {...register("designation")}
          />
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", mb: 0.8 }}>
            Default Capacity %
          </Typography>

          <TextField
            fullWidth
            size="small"
            type="number"
            placeholder="e.g. 100"
            inputProps={{ min: 0, max: 300 }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.paper" } }}
            {...register("default_capacity_percent", { valueAsNumber: true, min: 0, max: 300 })}
          />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: "text.primary",
              mb: 0.8
            }}
          >
            Role
          </Typography>

          <Controller
            name="role_name"
            control={control}
            defaultValue={employee?.role_name || "member"}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: "background.paper"
                  }
                }}
              >
                {roleOptions.map((roleOption) => (
                  <MenuItem key={roleOption.value} value={roleOption.value}>
                    {roleOption.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: "text.primary",
              mb: 0.8
            }}
          >
            Manager <Box component="span" sx={{ color: "#dc2626" }}>*</Box>
          </Typography>

          <Autocomplete
            size="small"
            loading={managersLoading}
            options={sortedManagers}
            getOptionLabel={(option) => option.employee_name || ""}
            filterOptions={(options, state) => filterByLabel(options, state.inputValue, (option) => option.employee_name)}
            isOptionEqualToValue={(option, value) => option.employee_id === value.employee_id}
            value={sortedManagers.find((manager) => manager.employee_id === managerId) || null}
            onChange={(event, newValue) => setValue("manager_id", newValue ? newValue.employee_id : "", { shouldValidate: true })}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search and select manager"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {managersLoading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.paper" } }}
          />
        </Box>

        <Box>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.8}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary" }}>
              Department <Box component="span" sx={{ color: "#dc2626" }}>*</Box>
            </Typography>
            <Tooltip title="Auto-fill department from designation">
              <IconButton
                size="small"
                onClick={handleSyncDepartment}
                sx={{ p: 0.4, color: "primary.main" }}
              >
                <SyncIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
          {syncMessage && (
            <Typography fontSize={11} color="text.secondary" mb={0.5}>{syncMessage}</Typography>
          )}

          <Autocomplete
            size="small"
            loading={departmentsLoading}
            options={sortedDepartments}
            getOptionLabel={(option) => option.department_name || ""}
            filterOptions={(options, state) => filterByLabel(options, state.inputValue, (option) => option.department_name)}
            isOptionEqualToValue={(option, value) => option.department_id === value.department_id}
            value={sortedDepartments.find((department) => department.department_id === departmentId) || null}
            onChange={(event, newValue) => setValue("department_id", newValue ? newValue.department_id : "", { shouldValidate: true })}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search and select department"
                error={Boolean(errors.department_id)}
                helperText={errors.department_id ? "Please select a department" : ""}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {departmentsLoading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
                sx={{
                  "& .MuiFormHelperText-root": {
                    minHeight: 20,
                    mt: 0.7
                  }
                }}
              />
            )}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.paper" } }}
          />
        </Box>
      </Box>
    </form>
  );
}

export default EmployeeForm;

